/**
 * Tier Bands — Single source of truth for tier-band thresholds and DOK expectations.
 *
 * AUDIT v1.4 / Sprint 4 (V2) — Consolidates band-resolution and DOK-by-band
 * tables that were previously duplicated in:
 *   - pipeline/stages/pedagogyStage.ts  (MIN_DOK_BY_TIER_BAND, MAX_DOK_BY_TIER_BAND)
 *   - practice/logic/pedagogicalGuard.ts (MIN_DOK_FOR_BAND, local getTierBand)
 *
 * The values matched today but were independently maintained — drift between
 * pipeline-side validation and runtime-guard would have caused stille
 * divergentie tussen "wat de governance zegt" en "wat de leerling ervaart".
 *
 * After this consolidation: any change to band thresholds or DOK floors/ceilings
 * happens here once, and both consumers automatically agree.
 *
 * NOTE: `getTierBand()` is intentionally re-exported from `pipeline/policyEngine`
 * to avoid breaking existing imports while making this file the canonical place
 * for tier-band reasoning.
 */

import { getTierBand } from './pipeline/policyEngine'

/** Re-export so consumers have a single import for all tier-band concerns. */
export { getTierBand }

/** Tier-band string literals — keep aligned with policyEngine.getTierBand(). */
export type TierBand = 'basic' | 'intermediate' | 'advanced' | 'expert'

/**
 * Minimum DOK level expected per tier band.
 * - basic (tiers 1-4):        DOK 1 — recall is fine
 * - intermediate (tiers 5-8): DOK 1 — recall still acceptable
 * - advanced (tiers 9-12):    DOK 2 — at least application
 * - expert (tiers 13-16):     DOK 3 — strategic thinking required
 *
 * Used by:
 *   - pipeline/stages/pedagogyStage.ts → checkDokAlignment()
 *   - practice/logic/pedagogicalGuard.ts → evaluateQuestion()
 */
export const MIN_DOK_BY_TIER_BAND: Record<TierBand, number> = {
  basic: 1,
  intermediate: 1,
  advanced: 2,
  expert: 3,
}

/**
 * Maximum DOK level "naturally expected" per tier band.
 * Higher DOK on lower-band questions is allowed but flagged as informational
 * (PEDAGOGY_DOK_TOO_HIGH) so authors can sanity-check unusually deep questions
 * on early tiers.
 */
export const MAX_DOK_BY_TIER_BAND: Record<TierBand, number> = {
  basic: 2,
  intermediate: 3,
  advanced: 4,
  expert: 4,
}

// NOTE: `getDokExpectations(band)` was removed in RC-3 (G-17, audit 2026)
// as a dead export — zero source-code consumers. Both DOK-by-band tables above
// remain in use by pedagogyStage and pedagogicalGuard directly. If a future
// consumer wants the combined min/max accessor, recreate a 3-line helper.
